/**
 * Hero Component for Puck Editor
 * 
 * Banner utama dengan CTA - cocok untuk landing page UMKM
 */

import type { ComponentConfig } from '@measured/puck'

export interface HeroProps {
  title: string
  subtitle?: string
  buttonText?: string
  buttonUrl?: string
  backgroundImage?: string
  backgroundColor: string
  textColor: 'light' | 'dark'
  alignment: 'left' | 'center' | 'right'
  height: 'small' | 'medium' | 'large' | 'full'
  overlay: boolean
}

const heightClasses = {
  small: 'py-16',
  medium: 'py-24',
  large: 'py-32',
  full: 'min-h-screen flex items-center',
}

const alignmentClasses = {
  left: 'text-left items-start',
  center: 'text-center items-center',
  right: 'text-right items-end',
}

// Validate URL to prevent XSS attacks
const isValidUrl = (url: string): boolean => {
  // Handle SSR/SSG where window is not available
  if (typeof window === 'undefined') {
    return url.startsWith('/') || url.startsWith('#') || /^https?:\/\//i.test(url)
  }
  try {
    const parsed = new URL(url, window.location.origin)
    return parsed.protocol === 'http:' || parsed.protocol === 'https:'
  } catch {
    return url.startsWith('/') || url.startsWith('#')
  }
}

// Check if URL is external (for rel and target attributes)
const isExternalUrl = (url: string): boolean => {
  return url ? !url.startsWith('/') && !url.startsWith('#') : false
}

export function Hero({
  title,
  subtitle,
  buttonText,
  buttonUrl,
  backgroundImage,
  backgroundColor,
  textColor,
  alignment,
  height,
  overlay,
}: HeroProps) {
  const textColorClass = textColor === 'light' ? 'text-white' : 'text-gray-900'
  const subtitleColorClass = textColor === 'light' ? 'text-white/90' : 'text-gray-600'
  const safeButtonUrl = buttonUrl && isValidUrl(buttonUrl) ? buttonUrl : '#'
  const isExternal = buttonUrl ? isExternalUrl(buttonUrl) : false

  return (
    <section
      role="region"
      aria-label={title}
      className={`relative bg-cover bg-center ${heightClasses[height]}`}
      style={{
        backgroundImage: backgroundImage ? `url(${backgroundImage})` : undefined,
        backgroundColor: backgroundColor || '#3b82f6',
      }}
    >
      {/* Overlay */}
      {overlay && backgroundImage && (
        <div className="absolute inset-0 bg-black/50" aria-hidden="true" />
      )}

      {/* Content */}
      <div
        className={`relative container mx-auto px-4 flex flex-col ${alignmentClasses[alignment]}`}
      >
        <h1 className={`text-4xl md:text-5xl lg:text-6xl font-bold mb-4 ${textColorClass}`}>
          {title}
        </h1>
        {subtitle && (
          <p className={`text-xl md:text-2xl mb-8 max-w-2xl ${subtitleColorClass}`}>
            {subtitle}
          </p>
        )}
        {buttonText && (
          <a
            href={safeButtonUrl}
            aria-label={buttonText}
            {...(isExternal && { rel: 'noopener noreferrer', target: '_blank' })}
            className="inline-block bg-primary-500 hover:bg-primary-600 text-white px-8 py-3 rounded-lg font-semibold transition-colors"
          >
            {buttonText}
          </a>
        )}
      </div>
    </section>
  )
}

export const HeroConfig: ComponentConfig<HeroProps> = {
  label: 'Hero Section',
  defaultProps: {
    title: 'Selamat Datang di Toko Kami',
    subtitle: 'Temukan produk berkualitas dengan harga terjangkau',
    buttonText: 'Lihat Produk',
    buttonUrl: '#products',
    backgroundImage: '',
    backgroundColor: '#3b82f6',
    textColor: 'light',
    alignment: 'center',
    height: 'medium',
    overlay: true,
  },
  fields: {
    title: {
      type: 'text',
      label: 'Judul',
    },
    subtitle: {
      type: 'textarea',
      label: 'Sub Judul',
    },
    buttonText: {
      type: 'text',
      label: 'Teks Tombol',
    },
    buttonUrl: {
      type: 'text',
      label: 'Link Tombol',
    },
    backgroundImage: {
      type: 'text',
      label: 'URL Gambar Latar',
    },
    backgroundColor: {
      type: 'text',
      label: 'Warna Latar (HEX)',
    },
    textColor: {
      type: 'radio',
      label: 'Warna Teks',
      options: [
        { label: 'Terang', value: 'light' },
        { label: 'Gelap', value: 'dark' },
      ],
    },
    alignment: {
      type: 'radio',
      label: 'Posisi',
      options: [
        { label: 'Kiri', value: 'left' },
        { label: 'Tengah', value: 'center' },
        { label: 'Kanan', value: 'right' },
      ],
    },
    height: {
      type: 'select',
      label: 'Tinggi',
      options: [
        { label: 'Kecil', value: 'small' },
        { label: 'Sedang', value: 'medium' },
        { label: 'Besar', value: 'large' },
        { label: 'Full Screen', value: 'full' },
      ],
    },
    overlay: {
      type: 'radio',
      label: 'Overlay Gelap',
      options: [
        { label: 'Ya', value: true },
        { label: 'Tidak', value: false },
      ],
    },
  },
  render: Hero,
}
